'use client'

import type { StaticImageData } from 'next/image'

import { cn } from '@/utilities/ui'
import React from 'react'

import type { Props as MediaProps } from '../types'

import { cssVariables } from '@/cssVariables'
import { getMediaUrl } from '@/utilities/getMediaUrl'

const { breakpoints } = cssVariables

export const ImageMedia: React.FC<MediaProps> = (props) => {
  const {
    alt: altFromProps,
    fill,
    pictureClassName,
    imgClassName,
    priority,
    fetchPriority,
    resource,
    size: sizeFromProps,
    src: srcFromProps,
    loading: loadingFromProps,
  } = props

  let width: number | undefined
  let height: number | undefined
  let alt = altFromProps
  let src: StaticImageData | string = srcFromProps || ''

  if (!src && resource && typeof resource === 'object') {
    const { alt: altFromResource, height: fullHeight, url, width: fullWidth, sizes } = resource

    width = fullWidth!
    height = fullHeight!
    alt = altFromResource || ''

    const cacheTag = resource.updatedAt

    // Use Payload's generated image sizes for srcSet
    const srcSet = sizes && Object.entries(sizes)
      .filter(([key]) => key !== 'thumbnail') // Exclude thumbnail from srcSet
      .map(([, sizeData]) => {
        if (typeof sizeData === 'object' && sizeData?.url && sizeData?.width) {
          return `${getMediaUrl(sizeData.url, cacheTag)} ${sizeData.width}w`
        }
        return null
      })
      .filter(Boolean)
      .join(', ')

    src = getMediaUrl(url, cacheTag)

    const loading = loadingFromProps || (!priority ? 'lazy' : undefined)

    // Use Payload's generated sizes for responsive images
    const sizesAttr = sizeFromProps
      ? sizeFromProps
      : Object.entries(breakpoints)
          .map(([, value]) => `(max-width: ${value}px) ${value}px`)
          .join(', ')

    if (fill) {
      return (
        <picture className={cn(pictureClassName)}>
          <img
            alt={alt || ''}
            className={cn(imgClassName, 'object-cover w-full h-full')}
            loading={loading}
            fetchPriority={fetchPriority}
            src={src}
            srcSet={srcSet || undefined}
            sizes={sizesAttr}
          />
        </picture>
      )
    }

    return (
      <picture className={cn(pictureClassName)}>
        <img
          alt={alt || ''}
          className={cn(imgClassName)}
          height={height}
          width={width}
          loading={loading}
          fetchPriority={fetchPriority}
          src={src}
          srcSet={srcSet || undefined}
          sizes={sizesAttr}
        />
      </picture>
    )
  }

  // Handle static images (fallback to basic img tag)
  const loading = loadingFromProps || (!priority ? 'lazy' : undefined)

  return (
    <picture className={cn(pictureClassName)}>
      <img
        alt={alt || ''}
        className={cn(imgClassName, fill ? 'object-cover w-full h-full' : '')}
        height={!fill ? height : undefined}
        width={!fill ? width : undefined}
        loading={loading}
        fetchPriority={fetchPriority}
        src={typeof src === 'string' ? src : src.src}
      />
    </picture>
  )
}
